import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  ListItem,
  ViewerWithCollapsibleHeader,
} from "../../../../../components/viewerWithCollapsibleHeader/ViewerWithCollapsibleHeader";
import { EMPTY_OBJECT } from "../../../../../helpers/constants";
import { getFullName, isNotEmpty } from "../../../../../helpers/utils";
import { useMediaQueries } from "../../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../redux/slices/appState/appStateSelector";
import { keyDetailsSelector } from "../../../../../redux/slices/project/projectSelector";
import { officeAndContactDetailsSiteCoreKeys } from "../../../../createProject/siteCore/SiteCoreKeys";
import { OfficeAndContactDetails } from "../../../modelsAndSchemas/models";

interface ViewOfficeAndContactDetailsProps {
  officeAndContactDetails: OfficeAndContactDetails | undefined;
  handleIndividualAccordionChange: (key: string) => void;
  isExpanded: boolean;
}

const ViewOfficeAndContactDetails: React.FC<
  ViewOfficeAndContactDetailsProps
> = ({
  officeAndContactDetails,
  handleIndividualAccordionChange,
  isExpanded,
}) => {
  const { fetchLabel, fetchComponentTitle } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const keyDetails = useSelector(keyDetailsSelector);
  const { isMobile, isTablet } = useMediaQueries();

  const getLabel = (key: string) => {
    return fetchLabel(
      officeAndContactDetailsSiteCoreKeys.officeAndContactDetails,
      officeAndContactDetailsSiteCoreKeys[
        key as keyof typeof officeAndContactDetailsSiteCoreKeys
      ],
    );
  };

  const [officeContactDetails, setOfficeContactDetails] = useState<ListItem[]>(
    [],
  );

  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  useEffect(() => {
    if (!siteCoreLoading) {
      if (officeAndContactDetails && isNotEmpty(officeAndContactDetails)) {
        setOfficeContactDetails([
          { label: "salesOffice", value: keyDetails?.salesOffice?.name },
          { label: "billingOffice", value: keyDetails?.billingOffice?.name },
          ...(!isMobile && !isTablet ? [EMPTY_OBJECT] : []),
          {
            label: "localClientRelationshipOwnerDropDown",
            value: getFullName(
              officeAndContactDetails.localClientRelationshipOwner?.firstName,
              officeAndContactDetails.localClientRelationshipOwner?.lastName,
              true,
            ),
          },
          {
            label: "billingContactDropDown",
            value: getFullName(
              officeAndContactDetails.billingContact?.firstName,
              officeAndContactDetails.billingContact?.lastName,
              true,
            ),
          },
          {
            label: "hrContactDropDown",
            value: getFullName(
              officeAndContactDetails.hrContact?.firstName,
              officeAndContactDetails.hrContact?.lastName,
              true,
            ),
          },
          {
            label: "projectManagerDropDown",
            value: Array.isArray(officeAndContactDetails.projectManager)
              ? officeAndContactDetails.projectManager.flatMap((manager) => ({
                  value: manager.firstName,
                  label: getFullName(manager.firstName, manager.lastName, true),
                }))
              : "",
          },
          {
            label: "projectCoordinatorDropDown",
            value: Array.isArray(officeAndContactDetails.projectCoordinator)
              ? officeAndContactDetails.projectCoordinator.flatMap(
                  (coordinator) => ({
                    value: coordinator.firstName,
                    label: getFullName(
                      coordinator.firstName,
                      coordinator.lastName,
                      true,
                    ),
                  }),
                )
              : "",
          },
        ]);
      }
    }
  }, [siteCoreLoading, officeAndContactDetails, keyDetails]);

  return (
    <ViewerWithCollapsibleHeader
      key={"officeAndContactDetails"}
      accordionTitle={fetchComponentTitle(
        officeAndContactDetailsSiteCoreKeys.officeAndContactDetails,
      )}
      items={officeContactDetails}
      onAccordionChange={() =>
        handleIndividualAccordionChange("officeAndContactDetails")
      }
      labelResolver={getLabel}
      isExpanded={isExpanded}
      showMoreARIALabel={commonARIALabel.showNMore}
      removeChipNameARIALabel={commonARIALabel.removeChipName}
      closePopoverARIALabel={commonARIALabel.closePopover}
    />
  );
};

export default ViewOfficeAndContactDetails;
